// src/components/screens/Officers.js
import {
  AlertCircle,
  Calendar,
  CheckCircle,
  Download,
  Edit2,
  Eye,
  Mail,
  Phone,
  RefreshCw,
  Search,
  Trash2,
  User,
  Users,
  X
} from 'lucide-react';
import { useEffect, useState } from 'react';
import { useAuth } from '../../context/AuthContext';
import apiService from '../../services/apiService';
import { FORM_MODES } from '../../utils/constants';
import { validateForm, validators } from '../../utils/validation';
import InputField from '../common/InputField';

const Officers = ({ showModal: externalShowModal, onCloseModal }) => {
  const { user } = useAuth();
  const [officers, setOfficers] = useState([]);
  const [filteredOfficers, setFilteredOfficers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [selectedOfficer, setSelectedOfficer] = useState(null);
  const [formMode, setFormMode] = useState(FORM_MODES.CREATE);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [sortBy, setSortBy] = useState('name');
  const [sortOrder, setSortOrder] = useState('asc');
  
  // Form state
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    mobile: '',
    role: 'Officer',
    accessCode: '',
    isActive: true
  });
  const [formErrors, setFormErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Handle external modal trigger (from header button)
  useEffect(() => {
    if (externalShowModal) {
      openModal(FORM_MODES.CREATE);
      onCloseModal?.();
    }
  }, [externalShowModal, onCloseModal]);

  // Validation rules
  const validationRules = {
    firstName: [validators.required, validators.minLength(2), validators.maxLength(50)],
    lastName: [validators.required, validators.minLength(2), validators.maxLength(50)],
    email: [validators.required, validators.email],
    mobile: [validators.required, validators.minLength(10), validators.maxLength(10)],
    accessCode: [validators.required, validators.minLength(3)]
  };

  // Fetch officers from API
  const fetchOfficers = async () => {
    try {
      setLoading(true);
      setError(null);

      console.log('Dashboard: Fetching data...');

      // Fetch officers and scans separately with individual error handling
      let officersResponse = null;
      let scansResponse = null;

      try {
        console.log('Dashboard: Fetching users...');
        officersResponse = await apiService.getUsers();
        console.log('Dashboard: Users response:', officersResponse);
      } catch (error) {
        console.error('Dashboard: Users API failed:', error);
        officersResponse = { success: false, data: [], error: error.message };
      }

      

      console.log('Dashboard: API responses received', {
        officers: officersResponse,
        scans: scansResponse
      });

      // Extract data from responses with proper null checks
      const allUsers = (officersResponse && officersResponse.success && officersResponse.data) ? officersResponse.data : [];
      const officers = allUsers.filter(user => user.role === 'Officer'); // Filter for officers only
      // const scans = (scansResponse && scansResponse.success && scansResponse.logs) ? scansResponse.logs : [];

      // Calculate statistics
      // const stats = calculateStats(officers, scans);

      setOfficers(officers)

      console.log('Dashboard: Data processed', { officers: officers.length });

      // If both APIs failed, show error
      if (!officersResponse.success && !scansResponse.success) {
        throw new Error('Both Users and Scans APIs failed');
      }

    } catch (error) {
      console.error('Dashboard: Error fetching data:', error);
      setError(error.message);
    } finally {
      setLoading(false);
    }
  };


  // Load officers on component mount
  useEffect(() => {
    fetchOfficers();
  }, []);

  // Generate next access code
  const generateAccessCode = () => {
    const timestamp = Date.now().toString().slice(-6);
    return `OFC${timestamp}`;
  };

  // Filter and sort officers
  useEffect(() => {
    let filtered = [...officers];

    // Search filter
    if (searchTerm) {
      const searchLower = searchTerm.toLowerCase();
      filtered = filtered.filter(officer =>
        `${officer.firstName} ${officer.lastName}`.toLowerCase().includes(searchLower) ||
        officer.officerCode.toLowerCase().includes(searchLower) ||
        officer.email.toLowerCase().includes(searchLower) ||
        officer.accessCode?.toLowerCase().includes(searchLower)
      );
    }

    // Status filter
    if (statusFilter !== 'all') {
      filtered = filtered.filter(officer => officer.isActive === true);
    }

    // Sort officers
    filtered.sort((a, b) => {
      let aValue, bValue;
      
      switch (sortBy) {
        case 'name':
          aValue = `${a.firstName} ${a.lastName}`?.toLowerCase();
          bValue = `${b.firstName} ${b.lastName}`?.toLowerCase();
          break;
        case 'email':
          aValue = a.email.toLowerCase();
          bValue = b.email.toLowerCase();
          break;
        case 'joinDate':
          aValue = new Date(a.joinDate);
          bValue = new Date(b.joinDate);
          break;
        default:
          aValue = a.id;
          bValue = b.id;
      }

      if (sortOrder === 'asc') {
        return aValue > bValue ? 1 : -1;
      } else {
        return aValue < bValue ? 1 : -1;
      }
    });

    setFilteredOfficers(filtered);
  }, [officers, searchTerm, statusFilter, sortBy, sortOrder]);

  // Handle form input changes
  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;

    // Mobile is entered as the 10-digit local number only — the +91 prefix
    // is fixed in the UI and prepended when the payload is built, matching
    // the format the backend requires (and what the mobile apps already send).
    const nextValue = name === 'mobile'
      ? value.replace(/[^0-9]/g, '').slice(0, 10)
      : (type === 'checkbox' ? checked : value);

    setFormData(prev => ({
      ...prev,
      [name]: nextValue
    }));

    // Clear field error on input
    if (formErrors[name]) {
      setFormErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }

    // Clear general error on any input change
    if (formErrors.general) {
      setFormErrors(prev => ({
        ...prev,
        general: ''
      }));
    }
  };

  // Open modal for create/edit/view
  const openModal = (mode, officer = null) => {
    setFormMode(mode);
    setSelectedOfficer(officer);
    
    if (mode === FORM_MODES.CREATE) {
      setFormData({
        firstName: '',
        lastName: '',
        email: '',
        mobile: '',
        role: 'Officer',
        accessCode: generateAccessCode(),
        isActive: true
      });
    } else if (officer) {
      setFormData({
        firstName: officer.firstName || '',
        lastName: officer.lastName || '',
        email: officer.email || '',
        mobile: (officer.mobile || '').replace(/^\+91/, ''),
        role: 'Officer',
        accessCode: officer.accessCode || officer.officerCode,
        isActive: officer.isActive !== undefined ? officer.isActive : officer.isActive
      });
    }
    
    setFormErrors({});
    setShowModal(true);
  };

  // Close modal
  const closeModal = () => {
    setShowModal(false);
    setSelectedOfficer(null);
    setFormData({
      firstName: '',
      lastName: '',
      email: '',
      mobile: '',
      role: 'Officer',
      accessCode: '',
      isActive: true
    });
    setFormErrors({});
    setIsSubmitting(false);
  };

  // Submit form
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validate form
    const { isValid, errors } = validateForm(formData, validationRules);
    
    if (!isValid) {
      setFormErrors(errors);
      return;
    }

    // Check for duplicate email (excluding current officer in edit mode)
    const emailExists = officers.some(officer => 
      officer.email === formData.email && 
      (formMode === FORM_MODES.CREATE || officer._id !== selectedOfficer?._id)
    );

    if (emailExists) {
      setFormErrors({ email: 'Email address is already in use' });
      return;
    }

    setIsSubmitting(true);

    try {
      let result;

      if (formMode === FORM_MODES.CREATE) {
        // Same endpoint the Dashboard's "Add Officer" flow uses — the server
        // generates and emails the access code itself.
        const createPayload = {
          firstName: formData.firstName,
          lastName: formData.lastName,
          email: formData.email,
          mobile: `+91${formData.mobile}`,
          role: 'Officer'
        };
        console.log('Creating officer:', createPayload);
        result = await apiService.registerEmployee(createPayload);
      } else {
        const updatePayload = {
          firstName: formData.firstName,
          lastName: formData.lastName,
          email: formData.email,
          mobile: `+91${formData.mobile}`,
          accessCode: formData.accessCode,
          isActive: formData.isActive
        };
        console.log('Updating officer:', selectedOfficer._id, updatePayload);
        result = await apiService.updateUser(selectedOfficer._id, updatePayload);
      }

      console.log('Officer save result:', result);

      // Refresh the officers list
      await fetchOfficers();
      closeModal();

    } catch (error) {
      console.error('Error saving officer:', error);
      setFormErrors({ general: `Failed to save officer: ${error.message}` });
    } finally {
      setIsSubmitting(false);
    }
  };

  // Delete officer with confirmation
  const handleDelete = async (officer) => {
    if (window.confirm(`Are you sure you want to delete ${officer.firstName} ${officer.lastName}?\n\nThis action cannot be undone.`)) {
      try {
        await apiService.deleteUser(officer._id);
        // Refresh the officers list
        await fetchOfficers();
      } catch (error) {
        console.error('Error deleting officer:', error);
        alert(`Failed to delete officer: ${error.message}`);
      }
    }
  };

  // Refresh officers data
  const handleRefresh = () => {
    fetchOfficers();
  };

  // Export officers data
  const handleExport = () => {
    const csvData = [
      ['Officer Code', 'Name', 'Email', 'Mobile', 'Status', 'Join Date'],
      ...filteredOfficers.map(officer => [
        officer.officerCode || officer.accessCode,
        `${officer.firstName} ${officer.lastName}`,
        officer.email,
        officer.mobile,
        officer.status,
        officer.joinDate
      ])
    ];

    const csvContent = csvData.map(row => row.join(',')).join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `officers_${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
    window.URL.revokeObjectURL(url);
  };

  if (loading) {
    return (
      <div className="space-y-6 animate-fade-in">
        <div className="flex items-center justify-between">
          <div className="space-y-2">
            <div className="skeleton h-8 w-40"></div>
            <div className="skeleton h-4 w-56"></div>
          </div>
          <div className="skeleton h-10 w-28 rounded-xl"></div>
        </div>
        <div className="bg-white dark:bg-gray-800 rounded-2xl border border-gray-200/50 dark:border-gray-700/50 p-6 space-y-4">
          {[0, 1, 2, 3, 4].map((i) => (
            <div key={i} className="flex items-center space-x-4">
              <div className="skeleton w-12 h-12 rounded-xl flex-shrink-0"></div>
              <div className="flex-1 space-y-2">
                <div className="skeleton h-4 w-1/4"></div>
                <div className="skeleton h-3 w-1/3"></div>
              </div>
              <div className="skeleton h-6 w-16 rounded-full"></div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <AlertCircle className="h-8 w-8 text-red-500 mx-auto mb-4" />
          <p className="text-red-600 dark:text-red-400 mb-4">Error loading officers: {error}</p>
          <button
            onClick={handleRefresh}
            className="px-4 py-2 bg-primary-500 text-white rounded-lg hover:bg-primary-600 transition-colors"
          >
            Try Again
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between animate-fade-in-up">
        <div>
          <h1 className="text-3xl font-bold bg-gradient-to-r from-gray-900 to-gray-600 dark:from-white dark:to-gray-300 bg-clip-text text-transparent">
            Officers
          </h1>
          <p className="text-gray-600 dark:text-gray-400 mt-1">
            Manage officer profiles ({officers.length} total)
          </p>
        </div>
        <div className="flex items-center space-x-3">
          <button
            onClick={handleRefresh}
            className="group px-4 py-2 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl hover:bg-gray-50 dark:hover:bg-gray-700 flex items-center space-x-2 transition-all duration-200 hover:scale-[1.03] active:scale-[0.98]"
          >
            <RefreshCw className="h-4 w-4 transition-transform duration-500 group-hover:rotate-180" />
            <span>Refresh</span>
          </button>
          <button
            onClick={handleExport}
            className="px-4 py-2 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl hover:bg-gray-50 dark:hover:bg-gray-700 flex items-center space-x-2 transition-all duration-200 hover:scale-[1.03] active:scale-[0.98]"
          >
            <Download className="h-4 w-4" />
            <span>Export CSV</span>
          </button>
          {/*<button 
            onClick={() => openModal(FORM_MODES.CREATE)}
            className="px-4 py-2 bg-gradient-to-r from-primary-500 to-accent-600 text-white rounded-xl hover:from-primary-600 hover:to-accent-700 flex items-center space-x-2 transition-all duration-200 shadow-lg"
          >
            <Plus className="h-4 w-4" />
            <span>Add Officer</span>
          </button>*/}
        </div>
      </div>

      {/* Filters and Search */}
      <div className="bg-white dark:bg-gray-800 rounded-2xl border border-gray-200/50 dark:border-gray-700/50 overflow-hidden shadow-sm">
        <div className="p-6 border-b border-gray-200/50 dark:border-gray-700/50 bg-gray-50/50 dark:bg-gray-900/50">
          <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4">
            <div className="flex flex-col sm:flex-row items-start sm:items-center space-y-3 sm:space-y-0 sm:space-x-4">
              {/* Search Input */}
              <div className="relative w-full sm:w-80">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
                <input
                  type="text"
                  placeholder="Search officers..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10 pr-4 py-2 w-full bg-white dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-xl focus:ring-2 focus:ring-primary-500 transition-all text-gray-900 dark:text-white placeholder-gray-500"
                />
              </div>
              
              {/* Filters */}
              <div className="flex flex-wrap gap-3">
                <select 
                  value={statusFilter}
                  onChange={(e) => setStatusFilter(e.target.value)}
                  className="px-4 py-2 bg-white dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-xl focus:ring-2 focus:ring-primary-500 text-gray-900 dark:text-white text-sm transition-all duration-200 hover:border-primary-300 dark:hover:border-primary-600"
                >
                  <option value="all">All Status</option>
                  <option value="active">Active</option>
                  <option value="inactive">Inactive</option>
                </select>

                <select 
                  value={`${sortBy}-${sortOrder}`}
                  onChange={(e) => {
                    const [newSortBy, newSortOrder] = e.target.value.split('-');
                    setSortBy(newSortBy);
                    setSortOrder(newSortOrder);
                  }}
                  className="px-4 py-2 bg-white dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-xl focus:ring-2 focus:ring-primary-500 text-gray-900 dark:text-white text-sm transition-all duration-200 hover:border-primary-300 dark:hover:border-primary-600"
                >
                  <option value="name-asc">Name A-Z</option>
                  <option value="name-desc">Name Z-A</option>
                  <option value="email-asc">Email A-Z</option>
                  <option value="joinDate-desc">Newest First</option>
                  <option value="joinDate-asc">Oldest First</option>
                </select>
              </div>
            </div>
            
            <div className="text-sm text-gray-600 dark:text-gray-400 font-medium">
              Showing {filteredOfficers.length} of {officers.length} officers
            </div>
          </div>
        </div>

        {/* Officers Table */}
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50 dark:bg-gray-700/50 border-b border-gray-200/50 dark:border-gray-600/50">
              <tr>
                <th className="px-6 py-4 text-left text-sm font-semibold text-gray-700 dark:text-gray-300">Officer</th>
                <th className="px-6 py-4 text-left text-sm font-semibold text-gray-700 dark:text-gray-300">Contact</th>
                <th className="px-6 py-4 text-left text-sm font-semibold text-gray-700 dark:text-gray-300">Status</th>
                <th className="px-6 py-4 text-left text-sm font-semibold text-gray-700 dark:text-gray-300">Join Date</th>
                <th className="px-6 py-4 text-left text-sm font-semibold text-gray-700 dark:text-gray-300">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200/50 dark:divide-gray-700/50">
              {filteredOfficers.length > 0 ? (
                filteredOfficers.map((officer, rowIndex) => (
                  <tr
                    key={officer._id || officer.id}
                    style={{ '--stagger': rowIndex }}
                    className="group hover:bg-gray-50/50 dark:hover:bg-gray-700/25 transition-all duration-200 stagger-in"
                  >
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center space-x-4">
                        <div className="w-12 h-12 bg-gradient-to-br from-primary-500 to-accent-600 rounded-xl flex items-center justify-center shadow-lg transition-transform duration-200 group-hover:scale-105">
                          <span className="text-white font-bold text-sm">{officer.firstName?.slice(0,1)+officer.lastName?.slice(0,1)}</span>
                        </div>
                        <div>
                          <div className="font-semibold text-gray-900 dark:text-white">
                            {officer.firstName} {officer.lastName}
                          </div>
                          <div className="text-sm text-gray-500 dark:text-gray-400 font-mono">
                            {officer.officerCode || officer.accessCode}
                          </div>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="space-y-1">
                        <div className="text-sm text-gray-900 dark:text-white flex items-center">
                          <Mail className="h-3 w-3 mr-2 text-gray-400" />
                          {officer.email}
                        </div>
                        <div className="text-sm text-gray-500 dark:text-gray-400 flex items-center">
                          <Phone className="h-3 w-3 mr-2 text-gray-400" />
                          {officer.mobile}
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`inline-flex px-3 py-1 text-xs font-semibold rounded-full ${
                        officer.isActive 
                          ? 'bg-emerald-100 text-emerald-800 dark:bg-emerald-900/50 dark:text-emerald-300' 
                          : 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300'
                      }`}>
                        {officer.isActive? 'Active' : 'Inactive'}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900 dark:text-white">
                        {new Date(officer.createdAt).toLocaleDateString()}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center space-x-2 opacity-60 group-hover:opacity-100 transition-opacity">
                        <button
                          onClick={() => openModal(FORM_MODES.VIEW, officer)}
                          className="p-2 rounded-lg bg-gray-100 hover:bg-gray-200 dark:bg-gray-700 dark:hover:bg-gray-600 text-gray-700 dark:text-gray-300 transition-all duration-200 hover:scale-110 active:scale-95"
                          title="View Details"
                        >
                          <Eye className="h-4 w-4" />
                        </button>
                        <button
                          onClick={() => openModal(FORM_MODES.EDIT, officer)}
                          className="p-2 rounded-lg bg-primary-100 hover:bg-primary-200 dark:bg-primary-900/50 dark:hover:bg-primary-900/70 text-primary-700 dark:text-primary-300 transition-all duration-200 hover:scale-110 active:scale-95"
                          title="Edit Officer"
                        >
                          <Edit2 className="h-4 w-4" />
                        </button>
                        <button
                          onClick={() => handleDelete(officer)}
                          className="p-2 rounded-lg bg-red-100 hover:bg-red-200 dark:bg-red-900/50 dark:hover:bg-red-900/70 text-red-700 dark:text-red-300 transition-all duration-200 hover:scale-110 active:scale-95"
                          title="Delete Officer"
                        >
                          <Trash2 className="h-4 w-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan="5" className="px-6 py-12 text-center text-gray-500 dark:text-gray-400">
                    <div className="flex flex-col items-center space-y-3">
                      <Users className="h-12 w-12 text-gray-300 dark:text-gray-600" />
                      <div>
                        <p className="text-lg font-medium">No officers found</p>
                        <p className="text-sm">Try adjusting your search criteria or add a new officer</p>
                      </div>
                    </div>
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Officer Modal */}
      {showModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm animate-fade-in" onClick={closeModal}>
          <div className="w-full max-w-2xl bg-white dark:bg-gray-800 rounded-3xl shadow-2xl border border-gray-200/50 dark:border-gray-700/50 overflow-hidden max-h-[90vh] overflow-y-auto animate-scale-in" onClick={(e) => e.stopPropagation()}>
            {/* Modal Header */}
            <div className="p-6 border-b border-gray-200/50 dark:border-gray-700/50 bg-gradient-to-r from-primary-50 to-accent-50 dark:from-primary-900/20 dark:to-accent-900/20">
              <div className="flex items-center justify-between">
                <h3 className="text-xl font-bold text-gray-900 dark:text-white">
                  {formMode === FORM_MODES.CREATE ? 'Add New Officer' :
                   formMode === FORM_MODES.EDIT ? 'Edit Officer' : 'Officer Details'}
                </h3>
                <button
                  onClick={closeModal}
                  className="p-2 rounded-xl hover:bg-white/50 dark:hover:bg-gray-700/50 transition-all duration-200 hover:rotate-90"
                >
                  <X className="h-5 w-5" />
                </button>
              </div>
            </div>
            
            {/* Modal Content */}
            <div className="p-6">
              {formErrors.general && (
                <div className="mb-6 p-4 rounded-xl border bg-red-50 dark:bg-red-900/20 border-red-200 dark:border-red-800 flex items-center space-x-3">
                  <AlertCircle className="h-5 w-5 text-red-600 dark:text-red-400 flex-shrink-0" />
                  <p className="text-sm font-medium text-red-700 dark:text-red-400">{formErrors.general}</p>
                </div>
              )}

              <div className="space-y-6">
                {/* Basic Information */}
                <div>
                  <h4 className="text-lg font-semibold text-gray-900 dark:text-white mb-4 flex items-center space-x-2">
                    <User className="h-5 w-5 text-primary-600 dark:text-primary-400" />
                    <span>Basic Information</span>
                  </h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <InputField
                      name="accessCode"
                      type="text"
                      label="Access Code"
                      value={formData.accessCode}
                      onChange={handleInputChange}
                      error={formErrors.accessCode}
                      disabled={formMode === FORM_MODES.VIEW}
                      required
                    />
                    <div className="md:col-span-1">
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                        Status <span className="text-red-500">*</span>
                      </label>
                      <select
                        name="isActive"
                        value={formData.isActive}
                        onChange={(e) => handleInputChange({
                          target: { name: 'isActive', value: e.target.value === 'true', type: 'select' }
                        })}
                        disabled={formMode === FORM_MODES.VIEW}
                        className="w-full px-4 py-3 bg-white dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-2xl focus:ring-2 focus:ring-primary-500 text-gray-900 dark:text-white disabled:opacity-50 disabled:bg-gray-50 dark:disabled:bg-gray-800"
                      >
                        <option value={true}>Active</option>
                        <option value={false}>Inactive</option>
                      </select>
                    </div>
                    
                    <InputField
                      name="firstName"
                      type="text"
                      label="First Name"
                      placeholder="Enter first name"
                      value={formData.firstName}
                      onChange={handleInputChange}
                      error={formErrors.firstName}
                      disabled={formMode === FORM_MODES.VIEW}
                      icon={User}
                      required
                    />
                    
                    <InputField
                      name="lastName"
                      type="text"
                      label="Last Name"
                      placeholder="Enter last name"
                      value={formData.lastName}
                      onChange={handleInputChange}
                      error={formErrors.lastName}
                      disabled={formMode === FORM_MODES.VIEW}
                      icon={User}
                      required
                    />
                  </div>
                </div>

                {/* Contact Information */}
                <div>
                  <h4 className="text-lg font-semibold text-gray-900 dark:text-white mb-4 flex items-center space-x-2">
                    <Mail className="h-5 w-5 text-green-600 dark:text-green-400" />
                    <span>Contact Information</span>
                  </h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <InputField
                      name="email"
                      type="email"
                      label="Email Address"
                      placeholder="Enter email address"
                      value={formData.email}
                      onChange={handleInputChange}
                      error={formErrors.email}
                      disabled={formMode === FORM_MODES.VIEW}
                      icon={Mail}
                      autoComplete="email"
                      required
                    />
                    
                    <InputField
                      name="mobile"
                      type="tel"
                      label="Mobile Number"
                      placeholder="9000000000"
                      value={formData.mobile}
                      onChange={handleInputChange}
                      error={formErrors.mobile}
                      disabled={formMode === FORM_MODES.VIEW}
                      prefix="+91"
                      autoComplete="tel"
                      maxLength={10}
                      required
                    />
                  </div>
                </div>

                {/* Additional Info for View Mode */}
                {formMode === FORM_MODES.VIEW && selectedOfficer && (
                  <div>
                    <h4 className="text-lg font-semibold text-gray-900 dark:text-white mb-4 flex items-center space-x-2">
                      <Calendar className="h-5 w-5 text-indigo-600 dark:text-indigo-400" />
                      <span>Officer Information</span>
                    </h4>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <div className="p-4 bg-gradient-to-br from-primary-50 to-primary-100 dark:from-primary-900/20 dark:to-primary-800/20 rounded-xl border border-primary-200/50 dark:border-primary-700/50">
                        <div className="flex items-center space-x-2 mb-2">
                          <Calendar className="h-4 w-4 text-primary-600 dark:text-primary-400" />
                          <span className="text-sm font-medium text-primary-700 dark:text-primary-300">Join Date</span>
                        </div>
                        <p className="text-lg font-bold text-primary-900 dark:text-primary-100">
                          {new Date(selectedOfficer.createdAt).toLocaleDateString('en-US', {
                            year: 'numeric',
                            month: 'long',
                            day: 'numeric'
                          })}
                        </p>
                        <p className="text-xs text-primary-600 dark:text-primary-400 mt-1">
                          {Math.floor((new Date() - new Date(selectedOfficer.createdAt)) / (1000 * 60 * 60 * 24))} days ago
                        </p>
                      </div>
                      
                      <div className="p-4 bg-gradient-to-br from-emerald-50 to-emerald-100 dark:from-emerald-900/20 dark:to-emerald-800/20 rounded-xl border border-emerald-200/50 dark:border-emerald-700/50">
                        <div className="flex items-center space-x-2 mb-2">
                          <User className="h-4 w-4 text-emerald-600 dark:text-emerald-400" />
                          <span className="text-sm font-medium text-emerald-700 dark:text-emerald-300">Role</span>
                        </div>
                        <p className="text-lg font-bold text-emerald-900 dark:text-emerald-100">
                          {selectedOfficer.role}
                        </p>
                        <p className="text-xs text-emerald-600 dark:text-emerald-400 mt-1">
                          Access Code: {selectedOfficer.accessCode}
                        </p>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>
            
            {/* Modal Footer */}
            <div className="p-6 border-t border-gray-200/50 dark:border-gray-700/50 flex justify-end space-x-4 bg-gray-50/50 dark:bg-gray-900/50">
              <button 
                onClick={closeModal}
                disabled={isSubmitting}
                className="px-6 py-3 bg-white dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-xl font-medium hover:bg-gray-50 dark:hover:bg-gray-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {formMode === FORM_MODES.VIEW ? 'Close' : 'Cancel'}
              </button>
              
              {formMode !== FORM_MODES.VIEW && (
                <button
                  onClick={handleSubmit}
                  disabled={isSubmitting}
                  className="px-6 py-3 bg-gradient-to-r from-primary-500 to-accent-600 text-white rounded-xl font-medium hover:from-primary-600 hover:to-accent-700 transition-all duration-200 shadow-lg hover:shadow-xl hover:scale-[1.02] active:scale-[0.98] flex items-center space-x-2 disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:scale-100"
                >
                  {isSubmitting ? (
                    <>
                      <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>
                      <span>Saving...</span>
                    </>
                  ) : (
                    <>
                      <CheckCircle className="h-4 w-4" />
                      <span>{formMode === FORM_MODES.CREATE ? 'Create Officer' : 'Update Officer'}</span>
                    </>
                  )}
                </button>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Officers;